import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import Button from './Button';
import Icon from './Icon';

// §2.6 Toast — replaces alert(). Bottom-left, one line, undo where the action is reversible.
// useToast()(message, { tone: 'ok'|'info'|'warn'|'danger', undo?: () => void, duration? })
const Ctx = createContext(null);
const GLYPH = { ok: CircleCheck, info: Info, warn: TriangleAlert, danger: TriangleAlert };

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const seq = useRef(0);
  const dismiss = useCallback(id => setItems(xs => xs.filter(x => x.id !== id)), []);
  const toast = useCallback((message, { tone = 'ok', undo, duration = 6000 } = {}) => {
    const id = ++seq.current;
    setItems(xs => [...xs.slice(-2), { id, message, tone, undo }]);
    if (duration) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);
  return (
    <Ctx.Provider value={toast}>
      {children}
      <div className="ui ui-toasts" role="status" aria-live="polite">
        {items.map(t => (
          <div key={t.id} className={`ui-toast ui-toast--${t.tone}`}>
            <Icon icon={GLYPH[t.tone]} />
            <span className="ui-toast__msg">{t.message}</span>
            {t.undo && <Button variant="quiet" onClick={() => { t.undo(); dismiss(t.id); }}>Undo</Button>}
            <Button variant="quiet" iconOnly icon={X} label="Dismiss" onClick={() => dismiss(t.id)} />
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const fn = useContext(Ctx);
  if (!fn) throw new Error('useToast needs <ToastProvider> at the app root');
  return fn;
}
